import React, { useState, useEffect, useMemo } from "react";

const Timer = ({ initialMinutes }) => {
  const minutes = Number(initialMinutes);
  const totalInitialSeconds = useMemo(() => minutes * 60, [minutes]);

  const [secondsRemaining, setSecondsRemaining] = useState(totalInitialSeconds);
  const [isFinished, setIsFinished] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    setSecondsRemaining(totalInitialSeconds);
    setIsFinished(false);
    setIsRunning(false);
  }, [totalInitialSeconds]);

  useEffect(() => {
    if (!isRunning || isFinished) return;
    if (totalInitialSeconds <= 0 && secondsRemaining <= 0) {
      setIsFinished(true);
      return;
    }

    const intervalId = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(intervalId);
          setIsFinished(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [isRunning, isFinished, secondsRemaining, totalInitialSeconds]);

  const handleToggleRun = () => {
    if (!isFinished) setIsRunning((prev) => !prev);
  };

  const handleReset = () => {
    setIsRunning(false);
    setIsFinished(false);
    setSecondsRemaining(totalInitialSeconds);
  };

  const formatTime = (totalSeconds) => {
    const min = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
    const sec = String(totalSeconds % 60).padStart(2, "0");
    return `${min}:${sec}`;
  };

  const timeDisplay = formatTime(secondsRemaining);

  // Styles (Scaled down)
  const baseButtonStyle = {
    padding: "6px 0",
    fontWeight: "bold",
    borderRadius: "8px",
    transition: "all 0.3s ease",
    fontSize: "0.75rem",
    boxShadow:
      "0 2px 3px -1px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)",
    cursor: "pointer",
    flex: 1,
  };

  let toggleStyle = { ...baseButtonStyle, color: "white" };
  let statusMessageStyle = { fontWeight: "medium", fontSize: "0.75rem" };

  if (isFinished) {
    toggleStyle.backgroundColor = "#ef4444";
    toggleStyle.opacity = 0.5;
    toggleStyle.cursor = "not-allowed";
    statusMessageStyle.color = "#dc2626";
    statusMessageStyle.fontWeight = "extrabold";
  } else if (isRunning) {
    toggleStyle.backgroundColor = "#f59e0b";
    statusMessageStyle.color = "#b45309";
  } else {
    toggleStyle.backgroundColor = "#034d28";
    statusMessageStyle.color = "#034d28";
  }

  const timerDisplayStyle = {
    fontSize: "2.25rem",
    fontFamily: "monospace",
    padding: "0.75rem",
    borderRadius: "0.5rem",
    transition: "all 0.5s ease",
    boxShadow: "inset 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    width: "100%",
    textAlign: "center",
    letterSpacing: "0.05em",
  };

  if (isFinished) {
    timerDisplayStyle.backgroundColor = "#fef2f2";
    timerDisplayStyle.color = "#b91c1c";
  } else {
    timerDisplayStyle.backgroundColor = "#ecfdf5";
    timerDisplayStyle.color = "#065f46";
  }

  const toggleButtonText = isFinished
    ? "Finished"
    : isRunning
    ? "Pause"
    : "Start";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        backgroundColor: "white",
        boxShadow:
          "0 10px 15px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
        borderRadius: "12px",
        maxWidth: "200px",
        margin: "1rem auto",
        border: "1px solid #e5e7eb",
        fontFamily: "sans-serif",
      }}
    >
      <h2
        style={{
          fontSize: "0.75rem",
          fontWeight: "800",
          color: "#1f2937",
          marginBottom: "0.75rem",
        }}
      >
        Dynamic Countdown
      </h2>

      <div style={timerDisplayStyle}>{timeDisplay}</div>

      <p
        style={{
          marginTop: "0.75rem",
          textAlign: "center",
          fontSize: "0.75rem",
          fontWeight: "500",
        }}
      >
        {isFinished ? (
          <span style={statusMessageStyle}>⏰ Time's Up!</span>
        ) : isRunning ? (
          `Counting down from ${minutes}m.`
        ) : (
          <span style={statusMessageStyle}>Paused at {timeDisplay}.</span>
        )}
      </p>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          marginTop: "1rem",
          gap: "0.5rem",
        }}
      >
        <button
          onClick={handleToggleRun}
          disabled={isFinished}
          style={toggleStyle}
        >
          {toggleButtonText}
        </button>

        <button
          onClick={handleReset}
          style={{
            ...baseButtonStyle,
            backgroundColor: "#e5e7eb",
            color: "#374151",
          }}
        >
          Reset
        </button>
      </div>

      <div
        style={{
          marginTop: "1rem",
          padding: "0.5rem",
          fontSize: "0.625rem",
          backgroundColor: "#eef2ff",
          borderRadius: "0.5rem",
          color: "#6366f1",
          width: "100%",
          textAlign: "center",
          border: "1px solid #c7d2fe",
        }}
      >
        Current Time Set: {initialMinutes}m
      </div>
    </div>
  );
};

export default Timer;
